import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Play, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { BackgroundVideo } from '../components/player/BackgroundVideo';
import SEOHead from '../components/SEOHead';
import { FilmContentSections } from '../components/ContentSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || url.includes('amazonaws.com') || url.includes('crystalmedia') || url.startsWith('/');
};

/* ─── Fade up ─── */
const FadeUp = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

const FilmLandingPage = () => {
  const [content, setContent] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [landingRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/film-landing`),
          fetch(`${API_URL}/api/content/settings`).catch(() => ({ json: () => ({}) })),
        ]);
        const landingData = await landingRes.json();
        const settingsData = await settingsRes.json();
        setContent(landingData);
        setSiteSettings(settingsData);
        const filmLogo = settingsData.film_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(filmLogo) ? filmLogo : '');
      } catch (e) {
        console.error('Failed to fetch film landing data:', e);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  if (!content) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 2 }}>
          <span style={{ fontFamily: '"Josefin Sans", sans-serif', fontWeight: 100 }} className="text-[#D4A574]/40 text-lg tracking-[0.3em]">LOADING</span>
        </motion.div>
      </div>
    );
  }

  const steps = (content.process_steps || []).sort((a, b) => a.order - b.order);

  return (
    <div className="bg-black min-h-screen text-white" data-testid="film-landing-page">
      <SEOHead
        title="Cinematic Wedding Films — Luxury Wedding Videographer"
        description="Crystal Media is a premier wedding videographer creating cinematic wedding films for Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural weddings in Toronto, Vancouver, Montreal, Houston, Miami, Texas, and across Canada and the USA. 8K quality, custom soundtracks, and story-first filmmaking."
        url="/film"
        keywords={['Cinematic Wedding Films', 'Wedding Videographer Toronto', 'Pakistani Wedding Highlights', 'Luxury Wedding Films Canada', '8K Wedding Films', 'Toronto wedding videography', 'cinematic wedding films', 'luxury wedding cinematography', 'Crystal Media films']}
        siteSettings={siteSettings}
      />
      <style>{`
        .gold-text { color: #D4A574; }
        .gold-border { border-color: rgba(212, 165, 116, 0.3); }
        .gold-glow { box-shadow: 0 0 30px rgba(212, 165, 116, 0.15); }
      `}</style>

      {/* Header */}
      <Header logoUrl={logoUrl} variant="dark" />

      {/* ─── HERO with video/image loop ─── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" data-testid="film-hero">
        {/* Background video or image */}
        {content.hero_video_url ? (
          <div className="absolute inset-0" data-testid="film-hero-video">
            <BackgroundVideo hlsUrl={content.hero_hls_url || ''} mp4Url={content.hero_video_url} poster={content.hero_fallback_image_url || ''} />
          </div>
        ) : content.hero_fallback_image_url ? (
          <img src={content.hero_fallback_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : null}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60" />

        {/* Gold ambient */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 40%, rgba(212, 165, 116, 0.08) 0%, transparent 60%)' }} />

        {/* Content */}
        <div className="relative z-10 text-center px-6">
          <motion.p
            initial={{ opacity: 0, letterSpacing: '0.2em' }}
            animate={{ opacity: 1, letterSpacing: '0.4em' }}
            transition={{ duration: 1.5, delay: 0.3 }}
            className="text-[10px] md:text-xs uppercase text-[#D4A574]/50 mb-6 tracking-[0.4em]"
          >
            Crystal Media Films
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5 }}
            className="font-presti text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-[0.05em] leading-[0.95] mb-8"
            style={{ textShadow: '0 0 60px rgba(212, 165, 116, 0.2)' }}
          >
            {content.hero_heading}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="text-white/40 text-sm md:text-base max-w-lg mx-auto mb-12 leading-relaxed"
          >
            {content.hero_subheading}
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/film/theatre"
              className="group flex items-center gap-3 px-10 py-4 border border-[#D4A574]/30 text-[#D4A574] text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#D4A574] hover:text-black transition-all duration-500"
              data-testid="film-enter-theatre-btn"
            >
              <Play size={16} className="group-hover:scale-110 transition-transform" />
              Enter Theatre
            </Link>
            <a
              href="#our-process"
              className="px-10 py-4 border border-white/15 text-white/50 text-xs tracking-[0.2em] uppercase hover:border-white/40 hover:text-white transition-all"
              data-testid="film-our-process-btn"
            >
              Our Process
            </a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <span className="text-[9px] tracking-[0.3em] uppercase text-white/20">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }} className="w-[1px] h-8 bg-gradient-to-b from-[#D4A574]/40 to-transparent" />
        </motion.div>
      </section>

      {/* ─── CONTENT SECTIONS ─── */}
      {content.sections && content.sections.length > 0 && (
        <FilmContentSections sections={content.sections} />
      )}

      {/* ─── INQUIRE CTA (between sections and process) ─── */}
      {content.sections && content.sections.length > 0 && (
        <section className="py-20 px-6 relative overflow-hidden" data-testid="film-inquire-cta">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(212,165,116,0.06) 0%, transparent 50%)' }} />
          <FadeUp className="text-center relative z-10">
            <h2 className="font-presti text-2xl sm:text-3xl md:text-4xl tracking-[0.05em] mb-4" style={{ textShadow: '0 0 30px rgba(212,165,116,0.15)' }}>
              Ready to begin?
            </h2>
            <p className="text-white/30 text-sm md:text-base max-w-md mx-auto mb-8 leading-relaxed">
              Set up a consultation and see if we are the right fit for you.
            </p>
            <Link
              to="/inquire"
              className="group inline-flex items-center gap-3 px-10 py-4 bg-[#D4A574] text-black text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#D4A574]/90 transition-all"
              data-testid="film-sections-inquire-btn"
            >
              <span>Inquire Now</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
        </section>
      )}

      {/* ─── OUR PROCESS ─── */}
      <section id="our-process" className="py-32 px-6 md:px-12 lg:px-24 relative" data-testid="film-process-section">
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 20% 50%, rgba(212, 165, 116, 0.05) 0%, transparent 50%)' }} />

        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#D4A574]/40 mb-4">How We Work</p>
            <h2 className="font-presti text-4xl sm:text-5xl md:text-6xl tracking-[0.05em] leading-[1.05] mb-6" style={{ textShadow: '0 0 40px rgba(212, 165, 116, 0.15)' }}>
              {content.process_heading}
            </h2>
            <p className="text-white/30 text-sm md:text-base max-w-xl mb-20 leading-relaxed">
              {content.process_subheading}
            </p>
          </FadeUp>

          <div className="space-y-0">
            {steps.map((step, i) => (
              <FadeUp key={step.id || i} delay={i * 0.08}>
                <div className="group flex items-start gap-6 md:gap-10 py-10 border-b border-white/5" data-testid={`film-process-step-${i}`}>
                  {/* Number */}
                  <span className="text-white/70 font-presti text-3xl md:text-4xl flex-shrink-0 w-16 group-hover:text-white transition-colors duration-500">
                    {step.number}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-presti text-xl md:text-2xl tracking-[0.1em] text-white/80 mb-3 group-hover:text-[#D4A574] transition-colors duration-500">
                      {step.title}
                    </h3>
                    <p className="text-white/30 text-sm md:text-base leading-relaxed max-w-xl">
                      {step.description}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-32 flex items-center justify-center px-6 relative overflow-hidden" data-testid="film-landing-cta">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(212, 165, 116, 0.06) 0%, transparent 50%)' }} />
        <FadeUp className="text-center relative z-10">
          <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl tracking-[0.05em] mb-6" style={{ textShadow: '0 0 40px rgba(212, 165, 116, 0.15)' }}>
            Ready to tell your story?
          </h2>
          <p className="text-white/30 text-sm md:text-base max-w-md mx-auto mb-10 leading-relaxed">
            Let us create a cinematic masterpiece of your most special day.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/inquire" className="group px-10 py-4 bg-[#D4A574] text-black text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#D4A574]/90 transition-all flex items-center gap-3" data-testid="film-landing-inquire-btn">
              Inquire Now
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/film/theatre" className="px-10 py-4 border border-[#D4A574]/30 text-[#D4A574] text-xs tracking-[0.2em] uppercase hover:bg-[#D4A574] hover:text-black transition-all duration-500" data-testid="film-landing-theatre-btn">
              Enter Theatre
            </Link>
          </div>
        </FadeUp>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/20">
            {new Date().getFullYear()} Crystal Media. Toronto, Canada.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/photo" className="text-[10px] tracking-[0.2em] uppercase text-white/20 hover:text-[#D4A574] transition-colors">Photo</Link>
            <Link to="/film" className="text-[10px] tracking-[0.2em] uppercase text-white/20 hover:text-[#D4A574] transition-colors">Film</Link>
            <Link to="/about" className="text-[10px] tracking-[0.2em] uppercase text-white/20 hover:text-[#D4A574] transition-colors">About</Link>
            <Link to="/inquire" className="text-[10px] tracking-[0.2em] uppercase text-white/20 hover:text-[#D4A574] transition-colors">Inquire</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FilmLandingPage;
